import { useRef } from 'react'
import clsx from 'clsx'
import ScoreRing from 'shared-components/ui/ScoreRing'
import { HOW_WE_SCORE_URL } from 'lib/dealhunt/constants'
import { SG_RATING_EXPLAINER } from 'lib/community/model'
import { DisclosureGroup, FloatingPanel, POPOVER_ZONE_ATTR, useDisclosureGroup } from './Popover'
import styles from './SgScoreRing.module.css'

/** Every score ring on a page shares one group: one overlay open at a time. */
export const sgScoreRingGroup = new DisclosureGroup()

interface SgScoreRingProps {
  /** The ring's fill on /10 and its figure ("8.6"). */
  score: number
  text: string
  /** The trigger's accessible name ("SoundGuys rating 8.6 out of 10, what it means"). */
  label: string
  /** Unique on the page (the row's note id): the overlay's id. */
  id: string
  className?: string
  /** 'small': the ≈28px ring with its figure only, no caption — inline in a By product group
   *  head (mockup board 2). The overlay still opens; the label still names it. */
  size?: 'base' | 'small'
}

/**
 * The SoundGuys rating on a community feed row: the review header's ring (shared `ScoreRing`,
 * dark disc, cyan→blue arc, white figure) over a "SoundGuys" caption. Hover, focus or a tap
 * opens the explainer (Popover primitive). The overlay renders only while open, so the shared
 * HTML holds the ring and its button only.
 */
const SgScoreRing: React.VFC<SgScoreRingProps> = ({
  score,
  text,
  label,
  id,
  className,
  size = 'base',
}) => {
  const anchor = useRef<HTMLSpanElement>(null)
  const { openId, triggerProps } = useDisclosureGroup(sgScoreRingGroup)
  const panelId = `sg-score-${id}`
  const open = openId === panelId
  const { onMouseEnter, onMouseLeave, ...buttonProps } = triggerProps(panelId, {
    interactive: true,
  })
  return (
    <span
      ref={anchor}
      className={clsx(styles.anchor, className)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...{ [POPOVER_ZONE_ATTR]: '' }}
    >
      <button
        type="button"
        className={clsx(styles.trigger, size === 'small' && styles.small)}
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        {...buttonProps}
      >
        <ScoreRing className={styles.ring} score={score} variant="base">
          {text}
        </ScoreRing>
        {size === 'base' && (
          <span className={styles.caption} aria-hidden="true">
            {'SoundGuys'}
          </span>
        )}
      </button>
      {open && (
        <FloatingPanel
          id={panelId}
          labelledBy={`${panelId}-title`}
          anchor={anchor}
          as="span"
          prefer="below"
          className={styles.panel}
        >
          <b id={`${panelId}-title`} className={styles.title}>
            {`SoundGuys rating ${text} / 10`}
          </b>
          <span className={styles.text}>{SG_RATING_EXPLAINER}</span>
          <a href={HOW_WE_SCORE_URL} className={styles.link}>
            {'How we score →'}
          </a>
        </FloatingPanel>
      )}
    </span>
  )
}

export default SgScoreRing
